/**
 * Markdown Utility Functions
 * 
 * Contains functions for validating, sanitizing and processing markdown content
 */ 

/**
 * Checks if a string contains any markdown syntax
 * @param {string} text - The text to check for markdown
 * @returns {boolean} - True if markdown is detected, false otherwise
 */
export const hasMarkdown = (text) => {
  if (!text) return false;
  
  // Regex patterns for common markdown elements
  const patterns = [
    /(\*\*|__)(.*?)\1/,            // Bold: **text** or __text__
    /(\*|_)(.*?)\1/,               // Italic: *text* or _text_
    /~~(.*?)~~/,                   // Strikethrough: ~~text~~
    /^#+\s+/m,                     // Headers: # Header
    /`(.*?)`/,                     // Inline code: `code`
    /^>.*$/m,                      // Blockquotes: > text
    /!\[(.*?)\]\((.*?)\)/,         // Images: ![alt](url)
    /\[(.*?)\]\((.*?)\)/,          // Links: [text](url)
    /^(\*|\+|-)\s+/m,              // Unordered lists: * item
    /^(\d+)(\.|,)\s+/m,            // Ordered lists: 1. item
  ];
  
  // Return true if any markdown pattern is found in the text
  return patterns.some(pattern => pattern.test(text));
};

/**
 * Handles Markdown text with special link syntax for story navigation
 * @param {string} text - The markdown text to process
 * @param {function} linkHandler - Function to handle navigation links
 * @returns {React.ReactNode[]} - Array of processed text blocks
 */
export const processTextWithLinks = (text, linkHandler) => {
  // This function would be used for more complex text processing
  // For now, we can rely on the component-specific implementation
  return text;
};

/**
 * Sanitize markdown content to prevent XSS
 * Basic implementation - for production, consider using a dedicated library like DOMPurify
 * @param {string} markdownText - The markdown text to sanitize
 * @returns {string} - Sanitized markdown text
 */
export const sanitizeMarkdown = (markdownText) => {
  if (!markdownText) return '';
  
  // Basic sanitization: remove script tags and on* event handlers
  return markdownText
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/ on\w+="[^"]*"/g, '')
    .replace(/ on\w+='[^']*'/g, '')
    .replace(/ on\w+=\w+/g, '');
};

export default {
  hasMarkdown,
  processTextWithLinks,
  sanitizeMarkdown
}; 